const BASE_URL = process.env.REACT_APP_API_URL || "/api";
export const BACKEND_ORIGIN = BASE_URL.replace(/\/api\/?$/, "");

export const resolveBackendUrl = (value) => {
  if (!value) return "";
  const url = String(value);
  if (url.startsWith("http://") || url.startsWith("https://")) return url;
  if (url.startsWith("/")) return `${BACKEND_ORIGIN}${url}`;
  return `${BACKEND_ORIGIN}/${url}`;
};

const readAuthHeaders = () => {
  const token = localStorage.getItem("token");
  const rawUser = localStorage.getItem("myasmita:auth-user");

  let user = null;
  try {
    user = rawUser ? JSON.parse(rawUser) : null;
  } catch {
    user = null;
  }

  const headers = {};

  if (token) headers.Authorization = `Bearer ${token}`;

  if (user) {
    if (user.user_id != null) headers["X-User-Id"] = String(user.user_id);
    const roleKey = user.system_role || user.primary_role || user.user_type || user.role;
    if (roleKey) headers["X-User-Type"] = String(roleKey);
    if (user.society_id != null) headers["X-Society-Id"] = String(user.society_id);
  }

  return headers;
};

const toQueryString = (params) => {
  if (!params) return "";
  const searchParams = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "") return;

    if (Array.isArray(value)) {
      value.forEach((item) => {
        if (item === undefined || item === null || item === "") return;
        searchParams.append(key, String(item));
      });
      return;
    }

    searchParams.set(key, String(value));
  });

  const qs = searchParams.toString();
  return qs ? `?${qs}` : "";
};

const normalizeHeaders = (headers) => {
  if (!headers) return {};
  if (headers instanceof Headers) return Object.fromEntries(headers.entries());
  return { ...headers };
};

const isFormData = (value) =>
  typeof FormData !== "undefined" && value instanceof FormData;

class ApiError extends Error {
  constructor(message, { status, data, url, method } = {}) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.data = data;
    this.url = url;
    this.method = method;
  }
}

const request = async (method, url, data, config = {}) => {
  const params = config?.params;
  const query = toQueryString(params);
  const fullUrl = url.startsWith("http") ? `${url}${query}` : `${BASE_URL}${url}${query}`;

  const baseHeaders = readAuthHeaders();
  const extraHeaders = normalizeHeaders(config?.headers);
  const headers = { ...baseHeaders, ...extraHeaders };

  const init = {
    method,
    headers,
  };

  const hasBody = !["GET", "HEAD"].includes(method);
  if (hasBody && data !== undefined) {
    if (isFormData(data)) {
      delete headers["Content-Type"];
      delete headers["content-type"];
      init.body = data;
    } else {
      if (!headers["Content-Type"] && !headers["content-type"]) {
        init.headers = { ...headers, "Content-Type": "application/json" };
      }
      init.body = JSON.stringify(data);
    }
  }

  const res = await fetch(fullUrl, init);

  const contentType = res.headers.get("content-type") || "";
  const isJson = contentType.includes("application/json");

  let payload = null;
  try {
    payload = isJson ? await res.json() : await res.text();
  } catch {
    payload = null;
  }

  if (!res.ok) {
    if (res.status === 401) {
      const isAuthEndpoint =
        url.includes("/auth/login") ||
        url.includes("/auth/otp/verify") ||
        url.includes("/auth/reset-password");

      if (!isAuthEndpoint && typeof window !== "undefined") {
        const hadToken = Boolean(localStorage.getItem("token"));
        if (hadToken) {
          localStorage.removeItem("token");
          localStorage.removeItem("myasmita:auth-user");
          const currentPath =
            window.location.pathname + window.location.search + window.location.hash;
          if (
            window.location.pathname !== "/" &&
            !window.location.pathname.startsWith("/reset-password")
          ) {
            window.location.href = `/?redirect=${encodeURIComponent(currentPath)}`;
          }
        }
      }
    }

    const message =
      (payload && typeof payload === "object" && payload.message) ||
      `Request failed (${res.status})`;
    throw new ApiError(message, {
      status: res.status,
      data: payload,
      url: fullUrl,
      method,
    });
  }

  return {
    data: payload,
    status: res.status,
    headers: Object.fromEntries(res.headers.entries()),
  };
};

const API = {
  get: (url, config) => request("GET", url, undefined, config),
  delete: (url, config) => request("DELETE", url, undefined, config),
  post: (url, data, config) => request("POST", url, data, config),
  put: (url, data, config) => request("PUT", url, data, config),
  patch: (url, data, config) => request("PATCH", url, data, config),
  ApiError,
};

export default API;
